import { useCallback, useState } from 'react';
import type { FormEvent, HTMLAttributes } from 'react';
import { useTranslations } from 'next-intl';
import Search from '@engineering/icons/search';
import { Ui, Button, Theme } from '@silvertours/front-shared';
import { analytics, DateRangePicker } from '@silvertours/front-entities';
import { SearchFormOptions } from './SearchFormOptions';
import { Logo } from './Logo';
import { SearchProvider, useSearchContext } from './context';
import { useSession } from './hooks';
import { matchErrors, useSubmitHandler } from './hooks/useSubmitHandler';

import { isOneWay } from '../SearchForm';
import {
  ButtonWrapper,
  DateErrorMessage,
  DepartureInput,
  DestinationInput,
  FooterLink,
  FormContainer,
  LocationErrorMessage,
  OptionsCloseIcon,
  OptionsOpenIcon,
  StyledBadge,
  StyledForm,
  Title,
  Wrapper,
  TryAgainButtonWrapper,
  PageHeadline,
  Subtitle,
} from './SearchForm.styles';
import { isFormError } from './context/types';
import { SearchProviderProps } from './context/Provider';
import { getOptionsParam } from './utils';
import { OneWayToggle } from './OneWayToggle';
import { AgeInput } from './AgeInput';
import { AgeDropdown } from './AgeDropdown';
import { getDays } from '../SearchWizard/datetime';

type SearchFormProps = {
  customColor?: string;
  stageTitle?: string;
  isWide?: boolean;
  isAffiliate?: boolean;
  isErrorPage?: boolean;
  showPoweredBy?: boolean;
  headline?: string;
} & Pick<HTMLAttributes<HTMLFormElement>, 'className'>;

const SearchForm = ({
  customColor,
  headline,
  isWide,
  isAffiliate,
  isErrorPage,
  stageTitle,
  showPoweredBy,
  className,
}: SearchFormProps) => {
  const {
    setCustomerAge,
    setDate,
    setError,
    setInputValue,
    setShowDestLocation,
    state,
    isInternalCall,
  } = useSearchContext();

  const t = useTranslations('features.stageLegacy.stage.searchForm');
  const submitHandler = useSubmitHandler();

  const { trackClickTimestamp, removeClickTimestamp } = useSession();
  const [loading, setLoading] = useState(false);

  const isMobileTouchDevice = Theme.useIsMobileTouchDevice();
  const [showOptions, setShowOptions] = useState(false);
  const [checkedOptions, setCheckedOptions] = useState<string[]>([]);
  const [shouldSuppressDateError, setShouldSuppressDateError] = useState(false);

  // Show or hide the options on the right side of the form
  const toggleOptions = () => {
    const showOptionsValue = !showOptions;
    setShowOptions(showOptionsValue);
    analytics.gtm.trackToggleMoreOptions(showOptionsValue);
  };

  const handleOptionsChange = (optionsId: string) => {
    let checked = checkedOptions;
    if (checked.includes(optionsId)) {
      checked = checked.filter(key => key !== optionsId);
    } else {
      checked = [...checked, optionsId];
    }
    setCheckedOptions(checked);
  };

  const onError = useCallback((error: any) => {
    setLoading(false);
    removeClickTimestamp();

    matchErrors(error).forEach(({ type: kind, message }) => {
      if (isFormError(kind)) {
        setError({ kind, message });
        analytics.gtm.trackSearchError(kind);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const parseDate = (date: string | undefined) =>
    date ? new Date(date) : new Date();

  const handleSearchSubmit = async () => {
    setLoading(true);
    trackClickTimestamp();

    await submitHandler(
      state,
      getOptionsParam(checkedOptions),
      () => {
        setLoading(false);
        analytics.gtm.trackSearchSubmit(isOneWay(state));
        analytics.generic.trackStartSearch(
          state,
          getDays(parseDate(state?.depDate), parseDate(state?.destDate)),
          isOneWay(state),
        );
      },
      onError,
    );
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (loading) {
      return;
    }

    handleSearchSubmit();
  };

  if (!state) {
    return null;
  }

  const toggleDestLocation = (forceToggle = false) => {
    analytics.generic.trackReturnOnDifferentLocationClick(forceToggle);
    setError({ kind: 'locationError', message: '' });

    // only on wide form we should clear the destination input
    if (isWide) {
      setInputValue('destination', '');
    }

    if (!isWide || forceToggle) {
      setShowDestLocation(!state.showDestLocation);
    }
  };

  const title = stageTitle || t('title');
  const showDestinationInput = !!state.showDestLocation;
  const showOneWayToggle = !state.showDestLocation || !!isWide;
  const showOptionsToggle =
    !isAffiliate && ((isWide && isInternalCall) || !isWide);
  const showLogo = !!isAffiliate && !!showPoweredBy && !isMobileTouchDevice;

  const hasLocationError = !!state.locationError;

  const getLocationError = () => {
    if (showDestinationInput) {
      if (!state.depLocation && !state.destLocation) {
        return t('errorTexts.locationBoth');
      }
      if (!state.destLocation) {
        return t('errorTexts.dest');
      }
    }
    return t('errorTexts.dep');
  };

  return (
    <Wrapper
      aria-labelledby="search-form-title"
      className={className}
      $isWide={isWide}
    >
      <Title id="search-form-title" $isWide={isWide} $customColor={customColor}>
        {title}
      </Title>

      <StyledForm
        onSubmit={handleSubmit}
        id="search"
        role="search"
        aria-label={t('formTitle')}
        $isWide={isWide}
        $isErrorPage={isErrorPage}
      >
        {!isAffiliate &&
          (isErrorPage ? (
            <Subtitle $isWide={isWide}>{t('subtitle')}</Subtitle>
          ) : (
            !!headline && <PageHeadline>{headline}</PageHeadline>
          ))}
        <StyledBadge
          badgeText={t('cancellationBadge')}
          tooltipTitle={t('cancellationBadge')}
          tooltipText={t.raw('cancellation.tooltip.text')}
          tooltipLabel={t('cancellation.tooltip.label')}
          onHover={() => analytics.gtm.trackCancellationHover()}
          $isWide={isWide}
        />
        <FormContainer
          $isWide={isWide}
          $hasDestinationInput={showDestinationInput}
          $hasMoreOptionsToggle={showOptionsToggle}
          $hasLocationError={hasLocationError}
          $hasDateError={!shouldSuppressDateError && !!state.dateError}
        >
          <DepartureInput
            id="dep"
            onServiceNumbersSubmit={handleSearchSubmit}
            direction="departure"
            label={t('departure.label')}
            clearButtonLabel={t('departure.clearButtonLabel')}
            placeholder={t('departure.placeholder')}
            icon={<Search />}
            ariaErrorMessage="location-error"
            cleanable
          />
          {showOneWayToggle && (
            <OneWayToggle
              type={isWide ? 'toggle' : 'checkbox'}
              label={t('destination.toggleLabel')}
              id="destSelect"
              checked={showDestinationInput}
              onChange={() => toggleDestLocation(true)}
            />
          )}
          {showDestinationInput && (
            <DestinationInput
              id="dest"
              direction="destination"
              label={t('destination.label')}
              clearButtonLabel={t('destination.clearButtonLabel')}
              placeholder={t('destination.placeholder')}
              icon={<Search />}
              ariaErrorMessage="location-error"
              cleanable={() => toggleDestLocation()}
            />
          )}
          {hasLocationError && (
            <LocationErrorMessage id="location-error" $isWide={isWide}>
              {getLocationError()}
            </LocationErrorMessage>
          )}
          <DateRangePicker
            from={new Date(state.depDate)}
            to={new Date(state.destDate)}
            invalid={!!state.dateError && !shouldSuppressDateError}
            onCalendarVisibilityChange={setShouldSuppressDateError}
            onDatesChange={dates => {
              setDate('departure', dates.from!);
              setDate('destination', dates.to!);
            }}
            vertical={isMobileTouchDevice}
            dateDisplay={isWide ? 'short' : 'default'}
          />
          {state.dateError && !shouldSuppressDateError && (
            <DateErrorMessage id="date-error" $isWide={isWide}>
              {state.dateError}
            </DateErrorMessage>
          )}
          {isWide ? (
            <AgeInput value={state.customerAge} onChange={setCustomerAge} />
          ) : (
            <AgeDropdown
              value={state.customerAge}
              onChange={setCustomerAge}
              label={t('age.label')}
            />
          )}
          <ButtonWrapper $customColor={customColor}>
            <Button
              id="find"
              loading={loading}
              aria-label={loading ? t('button.loadingLabel') : undefined}
              type="submit"
              fullWidth
              icon={<Search width="24" height="24" />}
              textId="find-button-text"
            >
              {t('button.label')}
            </Button>
          </ButtonWrapper>
          {state.axiosError && !isWide && (
            <Ui.ErrorMessage>{t('errorTexts.request')}</Ui.ErrorMessage>
          )}
          {state.axiosError && isWide && (
            <Ui.Overlay
              open
              title={t('errorTexts.requestTitle')}
              titlePosition="left"
              width="wide"
              onClose={() => setError({ kind: 'axiosError', message: '' })}
            >
              <p>{t('errorTexts.request')}</p>
              <TryAgainButtonWrapper>
                <Button
                  id="try-again"
                  textId="try-again-text"
                  onClick={() => {
                    setError({ kind: 'axiosError', message: '' });
                    handleSearchSubmit();
                  }}
                >
                  {t('tryAgainButton.label')}
                </Button>
              </TryAgainButtonWrapper>
            </Ui.Overlay>
          )}

          {showLogo && <Logo />}

          {showOptionsToggle && (
            <FooterLink iconOnLeft={showOptions}>
              <Ui.TextLink
                id="more-options-text"
                as="button"
                onClick={toggleOptions}
              >
                {isWide ? t('options.internal') : t('options.label')}{' '}
                {checkedOptions.length > 0 && `(${checkedOptions.length})`}
                {showOptions ? (
                  <OptionsOpenIcon id="more-options-open-icon" />
                ) : (
                  <OptionsCloseIcon id="more-options-close-icon" />
                )}
              </Ui.TextLink>
            </FooterLink>
          )}
        </FormContainer>

        {showOptions && (
          <SearchFormOptions
            onChange={handleOptionsChange}
            setShowOptions={setShowOptions}
            checked={checkedOptions}
            forceOverlay={!!isWide}
          />
        )}
      </StyledForm>
    </Wrapper>
  );
};

const SearchFormWithContext = ({
  pageData,
  ...formProps
}: SearchFormProps & SearchProviderProps) => (
  <SearchProvider pageData={pageData}>
    <SearchForm {...formProps} />
  </SearchProvider>
);

export { SearchFormWithContext as SearchForm };
